import { useContext, useEffect, useCallback } from 'react';
import QubFileGroup from './FileGroup';
import QubFileBundle from './FileBundle';
import QubFile from './File';
import { ctx } from './fileContext';
import { get, isNil } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { generic } from 'apis';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { id } from 'date-fns/locale';

const env = import.meta.env.NODE_ENV;

const useFileClient = (defaultFileGroups) => {
    const { fileGroups, update } = useContext(ctx);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const init = async (fileGroups) => {
        const promises = fileGroups.map(async (group) => {
            return await QubFileGroup.create(group);
        });
        const groups = await Promise.all(promises);
        update(groups);
    };

    useEffect(() => {
        if (!isNil(defaultFileGroups)) {
            init(defaultFileGroups);
        }
    }, [defaultFileGroups]);

    const addFileGroup = (group) => {
        update((prev) => [...prev, new QubFileGroup(group)]);
    };

    const removeGroup = (id) => {
        update((prev) => prev.filter((group) => group.id !== id));
    };

    const getFileGroups = () => {
        return fileGroups;
    };

    // thanks ChatGPT :)
    const base64ToFile = (base64String, fileName) => {
        // Split the base64 string in data and contentType
        const [base64Header, base64Data] = base64String.split(',');
        const mimeType = base64Header.match(/:(.*?);/)[1];

        // Decode the base64 string
        const byteCharacters = atob(base64Data);

        // Create an array of 8-bit unsigned integers
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        // Create a `Uint8Array` from the byte numbers array
        const byteArray = new Uint8Array(byteNumbers);

        // Create a `Blob` from the `Uint8Array`
        const blob = new Blob([byteArray], { type: mimeType });

        // Create a `File` from the `Blob`
        const file = new File([blob], fileName, { type: mimeType });

        return file;
    };

    const downloadFile = async (inputFile) => {
        try {
            let data;
            let file;
            if (inputFile.type === 'application/pdf') {
                data = await generic.downloadFile({ FileId: inputFile.id }, { responseType: 'blob' });
                file = new File([data], inputFile.name, { type: 'application/pdf' });
                return file;
            } else if (inputFile.type === 'image/jpeg' || type === 'image/png') {
                data = await generic.downloadFileAsBase64({ Id: inputFile.id });
                file = base64ToFile(data, inputFile.name);
                return file;
            } else {
                return new File([''], inputFile.name, { type: inputFile.type });
            }
        } catch (error) {
            console.error('Error downloading file', error);
        }
    };

    const getFileGroupsMapped = async () => {
        const mapped = await Promise.all(
            fileGroups?.map(async (group) => {
                const defaultBundles = await Promise.all(
                    group.bundles.map(async (bundle) => {
                        const defaultFiles = await Promise.all(
                            bundle.files.map(async (file) => {
                                return {
                                    id: uuidv4(),
                                    databaseId: file.id,
                                    name: file.name,
                                    saved: true,
                                    compressed: false,
                                    file: await downloadFile(file)
                                };
                            })
                        );

                        return {
                            id: bundle.id,
                            databaseId: bundle.id,
                            defaultValues: bundle.values,
                            saved: true,
                            defaultFiles
                        };
                    })
                );
                return {
                    id: group?.id,
                    title: group?.description,
                    fields: group?.formFields
                        .filter((field) => !!field.label)
                        .map((fld) => ({
                            name: fld.name,
                            label: fld.label,
                            options: fld?.options?.filter((opt) => !opt.options.hide),
                            isRequired: fld.required,
                            type: fld.type
                        })),
                    defaultBundles
                };
            })
        );

        return mapped;
    };

    const getFileGroupsMapped2 = useCallback(() => {
        console.log('fileGroups', fileGroups);
        const mapped = fileGroups?.map((group) => ({
            id: group?.id,
            title: group?.description,
            fields: group?.formFields
                .filter((field) => !!field.label)
                .map((fld) => ({
                    name: fld.name,
                    label: fld.label,
                    options: fld?.options?.filter((opt) => !opt.options.hide),
                    isRequired: fld.required,
                    type: fld.type
                })),
            defaultBundles: group?.bundles?.map((bundle) => ({
                id: bundle.id,
                databaseId: bundle.id,
                defaultValues: bundle.values,
                saved: true,
                defaultFiles: bundle?.files?.map((file) => ({
                    id: uuidv4(),
                    databaseId: file.id,
                    name: file.name,
                    saved: true,
                    compressed: false,
                    file: file.fileObject
                }))
            }))
        }));
        return mapped;
    }, [fileGroups]);

    const addFile = (file, bundleId) => {
        update((prev) =>
            prev.map((group) => {
                group.bundles.map((bundle) => {
                    if (bundle.id === bundleId) {
                        const newFile = new QubFile(file);
                        bundle.addFile(new QubFile({ ...file, fileObject: file.file }));
                    }
                    return bundle;
                });
                return group;
            })
        );
    };

    const getFileBundles = (groupId) => {
        const group = fileGroups.find((group) => group.id === groupId);
        if (isNil(group)) {
            return undefined;
        }
        return group.bundles;
    };

    const addFileBundle = (bundle, groupId) => {
        update((prev) =>
            prev.map((group) => {
                if (group.id === groupId) {
                    group.addBundle(new QubFileBundle(bundle));
                }
                return group;
            })
        );
    };

    const uploadBundle = async (groupId, files, fileForm, bundleId, token) => {
        try {
            const formData = new FormData();
            files.forEach((file, index) => {
                formData.append(`Files[${index}].Id`, file?.databaseId);
                formData.append(`Files[${index}].File`, file?.file);
                formData.append(`Files[${index}].OrigFName`, file?.name);
                formData.append(`Files[${index}].FileTypeId`, groupId);
                formData.append(`Files[${index}].FileName`, file?.name);
                formData.append(`Files[${index}].Comments`, fileForm?.comments);
                formData.append(`Files[${index}].DateFrom`, fileForm?.dateFrom);
                formData.append(`Files[${index}].DateTo`, fileForm?.dateTo);
                formData.append(`Files[${index}].IssuedAt`, fileForm?.issuedAt);
                formData.append(`Files[${index}].IssuedBy`, fileForm?.issuedBy);
                formData.append(`Files[${index}].Number`, fileForm?.number);
                formData.append(`Files[${index}].CommonId`, fileForm?.common);
                formData.append(`Files[${index}].BundleId`, bundleId);
                formData.append(`Files[${index}].RowGuid`, file?.id);
                formData.append(`Files[${index}].Position`, index + 1);
            });
            const options = isNil(token)
                ? ''
                : {
                      headers: {
                          Authorization: `Bearer ${token}`
                      }
                  };
            const fileIds = await generic.uploadFiles(formData, options);
            const bundles = getFileBundles(groupId);
            if (bundles && !bundles.some((bundle) => bundle?.id === bundleId)) {
                addFileBundle({ id: bundleId, values: fileForm }, groupId);
            }
            if (!!fileIds) {
                fileIds?.data?.forEach((fid) => {
                    const currFile = files.find((file) => file.id.toUpperCase() === fid.RowGuid);
                    if (!isNil(currFile)) {
                        addFile(
                            {
                                id: fid.Id,
                                name: currFile.name,
                                type: currFile.file.type,
                                file: currFile.file
                                // values: fileForm
                            },
                            bundleId
                        );
                    }
                });
            }
            // enqueueSnackbar(t('FileUploadSuccessful'), {
            //     variant: 'success'
            // });
            // return fileIds;
        } catch (error) {
            enqueueSnackbar(t('FileUploadError'), {
                variant: 'error'
            });
            return null;
        }
    };

    const removeFileBundle = (id) => {
        update((prev) =>
            prev.map((group) => {
                group.removeBundle(id);
                return group;
            })
        );
    };

    const deleteBundle = async (bundleId, projectTableId, recordId, recordGuid, token) => {
        try {
            if (recordGuid === null) {
                removeFileBundle(bundleId);
                return;
            }
            const options = isNil(token)
                ? ''
                : {
                      headers: {
                          Authorization: `Bearer ${token}`
                      }
                  };
            await generic.removeFile({ BundleId: bundleId, projectTableId, recordId, recordGuid }, options);
            enqueueSnackbar(t('DeleteFileSuccessful'), { variant: 'success' });
            removeFileBundle(bundleId);
            // queryClient.invalidateQueries('files');
        } catch (error) {
            enqueueSnackbar(t('DeleteFileError'), { variant: 'error' });
        }
    };

    const removeFile = (id) => {
        update((prev) =>
            prev.map((group) => {
                group.removeFile(id);
                return group;
            })
        );
    };

    const deleteFile = async (fileId, projectTableId, recordId, recordGuid, token) => {
        try {
            if (recordGuid === null && recordGuid === null) {
                removeFile(fileId);
                return;
            }
            const options = isNil(token)
                ? ''
                : {
                      headers: {
                          Authorization: `Bearer ${token}`
                      }
                  };
            await generic.removeFile({ FileId: fileId, projectTableId, recordId, recordGuid }, options);
            enqueueSnackbar(t('DeleteFileSuccessful'), { variant: 'success' });
            removeFile(fileId);
            // queryClient.invalidateQueries('files');
        } catch (error) {
            enqueueSnackbar(t('DeleteFileError'), { variant: 'error' });
        }
    };

    const getGroupFormFields = (id) => {
        const group = fileGroups.find((group) => group.id === id);
        if (isNil(group)) {
            return undefined;
        }
        return group.formFields;
    };

    const getFileBundle = (id) => {
        let group;
        // eslint-disable-next-line no-restricted-syntax
        for (group of fileGroups) {
            const bundle = group.getBundle(id);
            if (!isNil(bundle)) {
                return bundle;
            }
        }
        return undefined;
    };

    const getFile = (id) => {
        let group;
        // eslint-disable-next-line no-restricted-syntax
        for (group of fileGroups) {
            const file = group.getFile(id);
            if (!isNil(file)) {
                return file;
            }
        }
        return undefined;
    };

    const getFiles = () => {
        return fileGroups.reduce((prev, curr) => [...prev, ...curr?.bundles?.files], []);
    };

    return {
        fileGroups,
        init,
        getFileGroups,
        getFileGroupsMapped,
        getFileGroupsMapped2,
        getGroupFormFields,
        addFileGroup,
        addFile,
        getFile,
        removeFile,
        removeGroup,
        getFiles,
        addFileBundle,
        getFileBundle,
        removeFileBundle,
        deleteBundle,
        uploadBundle,
        deleteFile
    };
};

export default useFileClient;
